import type { MinimalTag } from "@/modules/tag/types";
import FormBuilderFlowTab from "./form-builder-flow-tab";
import FormBuilderSettingsTab from "./form-builder-settings-tab";
import type { useFormBuilderState } from "./hooks/use-form-builder-state";

interface TagSearchResult {
	tags: MinimalTag[];
	hasMore: boolean;
	nextCursor: string | null;
}

interface FormBuilderLeftPanelProps {
	formState: ReturnType<typeof useFormBuilderState>;
	tags: MinimalTag[];
	isLoading?: boolean;
	onCreateTag?: (label: string, onCreated: (tagId: string) => void) => void;
	hasMoreTags?: boolean;
	isLoadingMoreTags?: boolean;
	onLoadMoreTags?: () => void;
	onSearchTags?: (query: string, cursor?: string | null) => Promise<TagSearchResult | null>;
	onTagSelect?: (tag: MinimalTag) => void;
}

export default function FormBuilderLeftPanel({ formState, tags, isLoading = false, onCreateTag, hasMoreTags = false, isLoadingMoreTags = false, onLoadMoreTags, onSearchTags, onTagSelect }: FormBuilderLeftPanelProps) {
	const { activeTab, setActiveTab } = formState;

	const tabs = [
		{
			id: "flow" as const,
			label: "Flow",
			icon: (
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
					/>
				</svg>
			),
		},
		{
			id: "settings" as const,
			label: "Settings",
			icon: (
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
					/>
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
				</svg>
			),
		},
	];

	return (
		<div className="flex flex-col h-full">
			{/* Tab buttons */}
			<div className="flex border-b border-gray-200">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						onClick={() => setActiveTab(tab.id)}
						className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${
							activeTab === tab.id ? "text-gray-900" : "text-gray-500 hover:text-gray-700"
						}`}
					>
						{tab.icon}
						{tab.label}
						{activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />}
					</button>
				))}
			</div>

			{/* Tab content */}
			<div className="flex-1 overflow-y-auto">
				{activeTab === "flow" ? (
					<div className="p-6">
						<FormBuilderFlowTab formState={formState} isLoading={isLoading} />
					</div>
				) : (
					<FormBuilderSettingsTab formState={formState} tags={tags} isLoading={isLoading} onCreateTag={onCreateTag} hasMoreTags={hasMoreTags} isLoadingMoreTags={isLoadingMoreTags} onLoadMoreTags={onLoadMoreTags} onSearchTags={onSearchTags} onTagSelect={onTagSelect} />
				)}
			</div>
		</div>
	);
}
